import React, { useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Sparkle } from '@phosphor-icons/react';
import { readSSEStream } from '../utils/sse';
import NextDayPicksSection from './ai-ideas/NextDayPicksSection';
import CustomIdeaForm from './ai-ideas/CustomIdeaForm';
import StreamingIdeaResult from './ai-ideas/StreamingIdeaResult';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AIIdeas() {
  const { user, loading } = useAuth();
  const { tier } = useSubscription();

  const [symbol, setSymbol] = useState('');
  const [context, setContext] = useState('');
  const [tradeIdea, setTradeIdea] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [ideaError, setIdeaError] = useState('');
  const resultRef = useRef(null);

  const generateIdea = async () => {
    setIsGenerating(true);
    setTradeIdea('');
    setIdeaError('');

    // Auto-scroll result into view once user clicks generate
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);

    try {
      const response = await fetch(`${API}/ai/trade-ideas`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ symbol: symbol || null, market_context: context || null }),
      });

      if (response.status === 429) {
        const data = await response.json().catch(() => ({}));
        setIdeaError(data.detail || 'Daily limit reached. Upgrade to Pro or Elite for more ideas.');
        return;
      }
      if (!response.ok) {
        setIdeaError(`Unable to generate idea (status ${response.status}). Please try again.`);
        return;
      }

      await readSSEStream(response, (chunk) => {
        setTradeIdea((prev) => prev + chunk);
      });
    } catch (e) {
      console.error('trade-ideas stream failed', e);
      setIdeaError('Network error. Please check your connection and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0A0B0E' }}>
        <div className="text-white">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" />;

  return (
    <Layout>
      <div className="w-full max-w-[1920px] mx-auto px-6 py-8" data-testid="ai-ideas-page">
        <div className="flex items-center gap-3 mb-2">
          <Sparkle size={40} weight="duotone" className="text-[#A855F7]" />
          <h1 className="text-4xl font-heading font-black tracking-tighter text-white">AI Trade Ideas</h1>
        </div>
        <p className="text-sm text-[#94A3B8] mb-6">GPT-5.2 powered Indian market analysis • Nifty 50 focus</p>

        <NextDayPicksSection user={user} tier={tier} />

        <CustomIdeaForm
          symbol={symbol}
          setSymbol={setSymbol}
          context={context}
          setContext={setContext}
          onGenerate={generateIdea}
          isGenerating={isGenerating}
        />

        <StreamingIdeaResult
          ref={resultRef}
          tradeIdea={tradeIdea}
          isGenerating={isGenerating}
          ideaError={ideaError}
        />
      </div>
    </Layout>
  );
}
